import { apiClient } from "@base/api/client";
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dailyLogDetailQueryOptions } from "./useDailyLogDetailQuery";
import type { DailyLogLockStatus } from "./useDailyLogLockStatusQuery";
import { dailyLogLockStatusSchema } from "./useDailyLogLockStatusQuery";

const LOCK_URL = (projectId: string, logId: string) =>
  `/project-management/api/projects/${projectId}/daily-logs/${logId}/lock`;

async function acquireLock(projectId: string, logId: string): Promise<DailyLogLockStatus> {
  const response = await apiClient.post<unknown>(LOCK_URL(projectId, logId));
  return dailyLogLockStatusSchema.parse(response.data);
}

async function refreshLock(projectId: string, logId: string): Promise<DailyLogLockStatus> {
  const response = await apiClient.patch<unknown>(LOCK_URL(projectId, logId));
  return dailyLogLockStatusSchema.parse(response.data);
}

async function releaseLock(projectId: string, logId: string): Promise<void> {
  await apiClient.delete(LOCK_URL(projectId, logId));
}

export function useAcquireLockMutation(
  projectId: string,
  logId: string,
  options?: Omit<UseMutationOptions<DailyLogLockStatus, Error, void>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  const { onSuccess, ...otherOptions } = options ?? {};

  return useMutation({
    mutationFn: () => acquireLock(projectId, logId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey,
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["documents", "infinite", Number(projectId)], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["documents", projectId], refetchType: "all" });

      onSuccess?.(...args);
    },
    ...otherOptions,
  });
}

export function useRefreshLockMutation(
  projectId: string,
  logId: string,
  options?: Omit<UseMutationOptions<DailyLogLockStatus, Error, void>, "mutationFn">,
) {
  return useMutation({
    mutationFn: () => refreshLock(projectId, logId),
    ...options,
  });
}

export function useReleaseLockMutation(
  projectId: string,
  logId: string,
  options?: Omit<UseMutationOptions<void, Error, void>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  const { onSuccess, ...otherOptions } = options ?? {};

  return useMutation({
    mutationFn: () => releaseLock(projectId, logId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey,
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["documents", "infinite", Number(projectId)], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["documents", projectId], refetchType: "all" });

      onSuccess?.(...args);
    },
    ...otherOptions,
  });
}
